import React from 'react';
import { joinAriaIds, useWxbFieldIds } from '../_internal/fieldA11y';
import './FormField.css';

type WxbFormFieldChildProps = {
  id?: string;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  'aria-required'?: React.AriaAttributes['aria-required'];
};

export interface WxbFormFieldProps {
  label?: React.ReactNode;
  required?: boolean;
  error?: string;
  helpText?: string;
  children: React.ReactNode;
  className?: string;
  layout?: 'vertical' | 'horizontal';
  controlId?: string;
}

export const WxbFormField: React.FC<WxbFormFieldProps> = ({
  label,
  required,
  error,
  helpText,
  children,
  className = '',
  layout = 'vertical',
  controlId: providedControlId,
}) => {
  const child = React.isValidElement<WxbFormFieldChildProps>(children) && children.type !== React.Fragment
    ? children
    : null;
  const isNativeField = child && typeof child.type === 'string'
    ? ['input', 'select', 'textarea'].includes(child.type)
    : Boolean(child);
  const childControlId = child?.props.id;
  const { controlId, labelId, feedbackId } = useWxbFieldIds(providedControlId || childControlId);
  const hasFeedback = Boolean(error || helpText);
  const childDescribedBy = child?.props['aria-describedby'];
  const childLabelledBy = child?.props['aria-labelledby'];
  const describedBy = joinAriaIds(childDescribedBy, hasFeedback ? feedbackId : undefined);
  const labelledBy = joinAriaIds(childLabelledBy, label ? labelId : undefined);
  const enhancedChild = isNativeField && child
    ? React.cloneElement(child, {
      id: controlId,
      'aria-describedby': describedBy,
      'aria-labelledby': labelledBy,
      'aria-invalid': child.props['aria-invalid'] ?? (Boolean(error) || undefined),
      'aria-required': child.props['aria-required'] ?? (required || undefined),
    })
    : children;

  return (
    <div className={`wxb-form-field wxb-form-${layout} ${error ? 'has-error' : ''} ${className}`}>
      {label && (
        <label
          id={labelId}
          className="wxb-form-label"
          htmlFor={isNativeField ? controlId : undefined}
        >
          {label}
          {required && <span className="wxb-form-req" aria-hidden="true">*</span>}
        </label>
      )}
      <div
        className="wxb-form-control"
        role={!isNativeField && label ? 'group' : undefined}
        aria-labelledby={!isNativeField && label ? labelId : undefined}
        aria-describedby={!isNativeField ? describedBy : undefined}
      >
        {enhancedChild}
      </div>
      {error && <span id={feedbackId} className="wxb-form-error" role="alert">{error}</span>}
      {!error && helpText && <span id={feedbackId} className="wxb-form-help">{helpText}</span>}
    </div>
  );
};
